import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import React, { useMemo } from 'react';
import { OnSelect } from 'apps/sigma2-ui/src/lib/hooks/useDynamicFilters';
import { getFiltersForExtras } from 'apps/sigma2-ui/src/lib/hooks/useDynamicFilters';
import { LoadingSkeleton } from 'apps/sigma2-ui/src/components/LoadingSkeleton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { FlatCard } from 'apps/sigma2-ui/src/components/ui/FlatCard';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { FilterInformation } from 'apps/sigma2-ui/src/lib/hooks/filter-state';
import { UseDynamicFiltersPayload } from 'apps/sigma2-ui/src/lib/hooks/useDynamicFilters';
import cn from 'classnames';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';

interface Base {
  /**
   * Easy way to disable the button outside of the areStaticFiltersEqual function
   */
  canAnalyze?: boolean;
  onAnalyze: (filtersForExtras: FilterDefinitionForExtras[]) => void;
  /**
   * Slot for rendering additional filters managed outside of this component.
   * It will be rendered with the dynamic filters and have to manage its own state
   */
  staticFilters?: React.ReactNode;
  /**
   * Slot for actions like download and share
   */
  actions?: React.ReactNode;
  /**
   * Slot for footer content like dimension buttons, etc.
   */
  footer?: React.ReactNode;
  /**
   * flag for disabling the analyze button when the component is processing
   */
  isProcessing?: boolean;
  /**
   * flag for checking if the static filters have changed
   * @returns true if the static filters have changed
   */
  areStaticFiltersEqual: () => boolean;

  /**
   * Slim version of the filter bar, which will not take up the full width unless items wraps.
   */
  slim?: boolean;

  /**
   * Theme variant for the filter bar background style
   * By default, we use a light gray background, but this can be overridden with 'white'
   */
  bgVariant?: 'default' | 'white';

  /**
   * Choose between single and multi filters
   */
  filterType?: 'single' | 'multi';
  filterButtonText?: string;
}

export interface FilterBarPropsWithDynamicFilters extends Base {
  dynamicFiltersPayload: UseDynamicFiltersPayload | null;
}

interface FilterBarPropsWithoutDynamicFilters extends Base {
  dynamicFiltersPayload: 'none';
}

export type FilterBarProps =
  | FilterBarPropsWithDynamicFilters
  | FilterBarPropsWithoutDynamicFilters;

export const FilterSelectBox: React.FC<{
  type: 'single' | 'multi';
  filterInformation: FilterInformation;
  widthClass?: string;
  backgroundColor?: string;
  onSelect: OnSelect;
  isProcessing?: boolean;
}> = ({ filterInformation, onSelect, type, widthClass, isProcessing }) => {
  const list = useMemo(() => {
    return (
      filterInformation.values.map((value) => {
        return {
          optionLabel: value,
          optionValue: value,
        };
      }) ?? []
    );
  }, [filterInformation]);

  const isDisabled = useMemo(() => {
    return isProcessing !== undefined && isProcessing;
  }, [isProcessing]);

  return useMemo(() => {
    if (type === 'single') {
      return (
        <SelectBoxWithSearch
          type={type}
          list={list}
          widthClass={widthClass}
          label={filterInformation.displayName}
          setSelected={(value) => {
            if (!value) {
              return;
            }
            const headerValue = filterInformation.columnHeaderValue;
            if (!headerValue) {
              return;
            }
            onSelect({
              columnHeaderValue: headerValue,
              selectedValues: [value],
            });
          }}
          disabled={isDisabled}
          selected={filterInformation.selectedValues.at(0) ?? ''}
        />
      );
    } else if (type === 'multi') {
      return (
        <SelectBoxWithSearch
          type={type}
          list={list}
          widthClass={widthClass}
          label={filterInformation.displayName}
          setSelected={(values) => {
            if (typeof values === 'function' || !values) {
              return;
            }
            const headerValue = filterInformation.columnHeaderValue;
            if (!headerValue) {
              return;
            }
            onSelect({
              columnHeaderValue: headerValue,
              selectedValues: values,
            });
          }}
          selected={filterInformation.selectedValues}
          disabled={isDisabled}
        />
      );
    } else {
      throw guardExhaustive(type);
    }
  }, [filterInformation, isDisabled, list, onSelect, type, widthClass]);
};

export const FilterBar: React.FC<FilterBarProps> = ({
  filterType = 'multi',
  dynamicFiltersPayload,
  staticFilters,
  onAnalyze,
  areStaticFiltersEqual,
  canAnalyze = true,
  isProcessing,
  actions,
  footer,
  slim,
  bgVariant,
  filterButtonText = 'Filter',
}) => {
  const dynamicFilters = useMemo(() => {
    if (dynamicFiltersPayload === 'none') {
      return null;
    }

    return dynamicFiltersPayload?.dynamicFilters ?? null;
  }, [dynamicFiltersPayload]);

  const doneWaitingForDynamicFilters = useMemo(() => {
    return dynamicFiltersPayload === 'none' || !!dynamicFilters;
  }, [dynamicFilters, dynamicFiltersPayload]);

  const onSelect = useMemo(() => {
    if (dynamicFiltersPayload === 'none') {
      return null;
    }

    return dynamicFiltersPayload?.onSelect;
  }, [dynamicFiltersPayload]);

  const displayedFilters = useMemo(() => {
    if (!dynamicFilters || !onSelect) {
      return null;
    }

    return Object.entries(dynamicFilters).map(
      ([columnHeaderValue, filterInformation]) => {
        return (
          <FilterSelectBox
            key={columnHeaderValue}
            type={filterType}
            filterInformation={filterInformation}
            onSelect={onSelect}
            isProcessing={isProcessing}
          />
        );
      },
    );
  }, [dynamicFilters, isProcessing, filterType, onSelect]);

  const computedBgVariant = bgVariant ?? 'default';

  const allFiltersHaveAtLeastOneSelection = useMemo(() => {
    if (!dynamicFilters) {
      return true;
    }

    return Object.values(dynamicFilters).every(
      (filter) => filter.selectedValues.length > 0,
    );
  }, [dynamicFilters]);

  const disabled = useMemo(() => {
    if (!canAnalyze || isProcessing || !allFiltersHaveAtLeastOneSelection) {
      return false;
    }

    if (dynamicFiltersPayload === 'none') {
      return areStaticFiltersEqual();
    }

    if (dynamicFiltersPayload?.areDynamicFiltersEqual) {
      return (
        areStaticFiltersEqual() && dynamicFiltersPayload.areDynamicFiltersEqual
      );
    }

    return false;
  }, [
    allFiltersHaveAtLeastOneSelection,
    canAnalyze,
    isProcessing,
    dynamicFiltersPayload,
    areStaticFiltersEqual,
  ]);

  const CardContents = (
    <Spacing direction="vertical" size="md">
      <Spacing direction="horizontal" size="md">
        {doneWaitingForDynamicFilters && (
          <Spacing direction="horizontal" size="sm" className="flex-wrap">
            {staticFilters}

            {displayedFilters}
          </Spacing>
        )}

        {!doneWaitingForDynamicFilters && (
          <Spacing direction="horizontal" size="sm">
            <LoadingSkeleton widthClassName="w-40" heightClassName="h-12" />
            <LoadingSkeleton widthClassName="w-40" heightClassName="h-12" />
            <LoadingSkeleton widthClassName="w-40" heightClassName="h-12" />
          </Spacing>
        )}

        <Spacing direction="horizontal" size="md" className="items-start">
          <Spacing direction="horizontal" size="md" className="items-center">
            <div>
              <LegacyButton
                variant={LegacyButtonVariant.ANALYZE_BUTTON}
                className="px-5"
                onClick={() => {
                  if (doneWaitingForDynamicFilters && !isProcessing) {
                    onAnalyze(getFiltersForExtras(dynamicFilters));

                    if (dynamicFiltersPayload !== 'none') {
                      dynamicFiltersPayload?.onSubmit(dynamicFilters);
                    }
                  }
                }}
                disabled={disabled}
              >
                {filterButtonText}
              </LegacyButton>
            </div>

            {actions}
          </Spacing>
        </Spacing>
      </Spacing>

      {footer}
    </Spacing>
  );
  return (
    <Spacing direction="horizontal" size="md">
      {computedBgVariant === 'default' ? (
        <FlatCard
          className={cn('max-w-fit bg-[#fafafa]', {
            'w-full': !slim,
          })}
          paddingClassName={'p-4'}
        >
          {CardContents}
        </FlatCard>
      ) : (
        <Card
          className={cn('max-w-fit bg-white', {
            'w-full': !slim,
          })}
          paddingClassName={'p-4'}
        >
          {CardContents}
        </Card>
      )}
    </Spacing>
  );
};
